// src/mocks/handlers/account/financialReportsHandler.ts

import { rest } from 'msw';
import financialReportsData from '@/mocks/data/financialReportsData.json';
import { balanceActivityReportCsv } from '@/mocks/data/financialReportsBalanceActivityCsv';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';
import {
  IFinancialReport,
  IFinancialReportCreateRequestBody,
  IFinancialReportsCreateResponse,
  IFinancialReportsListResponse,
} from '@/domains/funding/financialReports.types';

const contentTypeByFormat: Record<string, string> = {
  CSV: 'text/csv',
  EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PDF: 'application/pdf',
};

const reports = financialReportsData.items as IFinancialReport[];

export const financialReportsHandler = rest.get<IFinancialReportsListResponse>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.ACCOUNT_FINANCIAL_REPORTS}`,
  (req, res, ctx) => {
    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    return res(ctx.status(200), ctx.json({ items: reports }));
  },
);

export const financialReportsCreateHandler = rest.post<
  IFinancialReportCreateRequestBody,
  { customer_uuid: string }
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.ACCOUNT_FINANCIAL_REPORTS_CREATE}`,
  async (req, res, ctx) => {
    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    let body: IFinancialReportCreateRequestBody;
    try {
      body = await req.json();
    } catch {
      return res(ctx.status(400), ctx.json({ error: 'Invalid JSON body' }));
    }

    const extension =
      body.file_format === 'PDF'
        ? 'pdf'
        : body.file_format === 'EXCEL'
          ? 'xlsx'
          : 'csv';

    const newReport: IFinancialReport = {
      id: `frpt_${Date.now()}`,
      ppcr_uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      type: body.type,
      status: 'PENDING',
      file_format: body.file_format,
      file_name:
        body.file_name ||
        `${body.type.toLowerCase().replace(/_/g, '-')}.${extension}`,
      report_expires_at: null,
      report_version: '1',
      report_parameters: {
        currencies: body.currencies,
        from_date: body.from_date,
        to_date: body.to_date,
        time_zone: 'UTC',
        transaction_types:
          body.type === 'BALANCE_ACTIVITY_REPORT'
            ? (body.transaction_types ?? null)
            : null,
      },
    };

    reports.unshift(newReport);

    const response: IFinancialReportsCreateResponse = { items: [newReport] };

    return res(ctx.status(200), ctx.json(response));
  },
);

export const financialReportsDownloadHandler = rest.get(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.ACCOUNT_FINANCIAL_REPORTS_DOWNLOAD}`,
  (req, res, ctx) => {
    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const { ppcr_uuid, report_id } = req.params;
    const report = reports.find(
      (item) => item.id === report_id && item.ppcr_uuid === ppcr_uuid,
    );

    if (!report) {
      return res(ctx.status(404), ctx.json({ error: 'Report not found' }));
    }

    if (report.status !== 'COMPLETED') {
      return res(ctx.status(400), ctx.json({ error: 'Report not ready' }));
    }

    const filename =
      report.file_name ||
      `financial-report-${report_id}.${report.file_format?.toLowerCase() ?? 'csv'}`;
    const contentType =
      contentTypeByFormat[report.file_format ?? 'CSV'] ?? 'text/csv';

    let content: string;
    if (report.type === 'BALANCE_ACTIVITY_REPORT') {
      content = balanceActivityReportCsv;
    } else {
      content = `Account statement report ${report_id}\nPeriod: ${report.report_parameters?.from_date ?? ''} to ${report.report_parameters?.to_date ?? ''}`;
    }

    return res(
      ctx.status(200),
      ctx.set('Content-Type', contentType),
      ctx.set('Content-Disposition', `attachment; filename="${filename}"`),
      ctx.body(content),
    );
  },
);
